import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import Draggable from "react-draggable";
import ReactTooltip from "react-tooltip";
import styled, { css } from "styled-components";

const stripPossibleThumb = imgObj => {
    if (!imgObj) return "";
    const { s3_thumb_key, s3_thumbnail_key, s3_preview_key, s3_eimage_thumb_key, s3_image_key, s3_eimage_key } = imgObj || {};
    return s3_eimage_thumb_key || s3_thumb_key || s3_thumbnail_key || s3_preview_key || s3_eimage_key || s3_image_key || "";
};

const isTypingInField = () => {
    const el = document.activeElement;
    if (!el) return false;
    const tag = el.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
    if (el.isContentEditable) return true;
    if (el.closest?.(".ck-editor__editable, [role='textbox']")) return true;
    return false;
};

export const ImageViewV2 = ({ onCancel = () => {}, initialImages = [], hasNextPrev = true, hasExport = true, isSurveyAssetView = false, noOverlay = false }) => {
    const [imageList, setImageList] = useState([]);
    const [selectedImage, setSelectedImage] = useState(null);
    const [rotation, setRotation] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [twEpoch, setTwEpoch] = useState(0);

    const twResetRef = useRef(null);

    const viewerContainerRef = useRef(null);
    const viewerShellRef = useRef(null);
    const thumbRowRef = useRef(null);

    const imgNaturalRef = useRef({ w: 0, h: 0 });

    const [renderSize, setRenderSize] = useState({ w: 0, h: 0 });

    useEffect(() => {
        if (Array.isArray(initialImages)) setImageList(initialImages);
        else setImageList([]);
    }, [initialImages]);

    useEffect(() => {
        if (!imageList?.length) return;
        if (!selectedImage || !imageList.some(it => (it?.id ?? it?._id) === (selectedImage?.id ?? selectedImage?._id))) {
            setSelectedImage(imageList[0]);
            setRotation(0);
        }
    }, [imageList]);

    const masterImage = useMemo(() => {
        if (!selectedImage) return null;
        return isSurveyAssetView ? selectedImage : selectedImage?.master_image;
    }, [selectedImage, isSurveyAssetView]);

    const imageUrl = useMemo(() => {
        if (!masterImage) return "";
        const { is_edited, s3_eimage_key, s3_image_key } = masterImage || {};
        return is_edited ? s3_eimage_key : s3_image_key;
    }, [masterImage]);

    useEffect(() => {
        if (!imageUrl) return;
        const img = new Image();
        img.onload = () => {
            imgNaturalRef.current = { w: img.naturalWidth || 0, h: img.naturalHeight || 0 };
            ReactTooltip.rebuild();
            recomputeRenderSize();
        };
        img.src = imageUrl;
    }, [imageUrl]);

    useEffect(() => {
        ReactTooltip.rebuild();
    }, [selectedImage]);

    useEffect(() => {
        if (!selectedImage || !thumbRowRef.current || !imageList?.length) return;
        const container = thumbRowRef.current;
        const idx = getIdx(selectedImage);
        if (idx < 0) return;
        const el = container.children?.[idx];
        if (!el) return;
        const elLeft = el.offsetLeft;
        const elWidth = el.offsetWidth;
        const containerWidth = container.clientWidth;
        const target = Math.max(0, elLeft - (containerWidth - elWidth) / 2);
        container.scrollTo({ left: target, behavior: "smooth" });
    }, [selectedImage, imageList]);

    const rotateLeft = () => {
        setRotation(r => (r - 90 + 360) % 360);
        setTwEpoch(e => e + 1);
        requestAnimationFrame(() => {
            recomputeRenderSize();
        });
    };

    const rotateRight = () => {
        setRotation(r => (r + 90) % 360);
        setTwEpoch(e => e + 1);
        requestAnimationFrame(() => {
            recomputeRenderSize();
        });
    };

    const resetRotation = () => {
        setRotation(0);
        setTwEpoch(e => e + 1);
        requestAnimationFrame(() => {
            recomputeRenderSize();
        });
    };

    const getIdx = img => imageList.findIndex(it => (it?.id ?? it?._id) === (img?.id ?? img?._id));

    const handleNext = useCallback(() => {
        if (!selectedImage) return;
        const idx = getIdx(selectedImage);
        if (idx >= 0 && idx < imageList.length - 1) {
            setSelectedImage(imageList[idx + 1]);
            setRotation(0);
            requestAnimationFrame(() => {
                twResetRef.current && twResetRef.current();
                recomputeRenderSize();
            });
        }
    }, [selectedImage, imageList]);

    const handlePrev = useCallback(() => {
        if (!selectedImage) return;
        const idx = getIdx(selectedImage);
        if (idx > 0) {
            setSelectedImage(imageList[idx - 1]);
            setRotation(0);
            requestAnimationFrame(() => {
                twResetRef.current && twResetRef.current();
                recomputeRenderSize();
            });
        }
    }, [selectedImage, imageList]);

    const handleThumbClick = useCallback(img => {
        setSelectedImage(img);
        setRotation(0);
        requestAnimationFrame(() => {
            twResetRef.current && twResetRef.current();
            recomputeRenderSize();
        });
    }, []);

    const toggleFullscreen = useCallback(async () => {
        try {
            if (!document.fullscreenElement) await viewerContainerRef.current?.requestFullscreen?.();
            else await document.exitFullscreen?.();
        } catch {}
    }, []);

    useEffect(() => {
        const onFsChange = () => {
            setIsFullscreen(Boolean(document.fullscreenElement));
            requestAnimationFrame(() => {
                twResetRef.current && twResetRef.current();
                recomputeRenderSize();
            });
        };
        document.addEventListener("fullscreenchange", onFsChange);
        return () => document.removeEventListener("fullscreenchange", onFsChange);
    }, []);

    const recomputeRenderSize = useCallback(() => {
        const shell = viewerShellRef.current;
        const nat = imgNaturalRef.current;
        if (!shell || !nat.w || !nat.h) {
            setRenderSize({ w: 0, h: 0 });
            return;
        }
        const cw = shell.clientWidth;
        const ch = shell.clientHeight;

        const rot = ((rotation % 360) + 360) % 360;
        const rot90 = rot === 90 || rot === 270;

        const s = rot90 ? Math.min(cw / nat.h, ch / nat.w) : Math.min(cw / nat.w, ch / nat.h);

        const w = (rot90 ? nat.h : nat.w) * s;
        const h = (rot90 ? nat.w : nat.h) * s;

        setRenderSize({ w, h });
    }, [rotation]);

    useEffect(() => {
        const onFsChange = () => {
            setIsFullscreen(Boolean(document.fullscreenElement));

            setTwEpoch(e => e + 1);

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    twResetRef.current && twResetRef.current();
                    recomputeRenderSize();
                });
            });
        };

        document.addEventListener("fullscreenchange", onFsChange);
        document.addEventListener("webkitfullscreenchange", onFsChange);
        document.addEventListener("mozfullscreenchange", onFsChange);
        document.addEventListener("MSFullscreenChange", onFsChange);

        return () => {
            document.removeEventListener("fullscreenchange", onFsChange);
            document.removeEventListener("webkitfullscreenchange", onFsChange);
            document.removeEventListener("mozfullscreenchange", onFsChange);
            document.removeEventListener("MSFullscreenChange", onFsChange);
        };
    }, [recomputeRenderSize]);

    useEffect(() => {
        const onResize = () => {
            requestAnimationFrame(() => {
                twResetRef.current && twResetRef.current();
                recomputeRenderSize();
            });
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [recomputeRenderSize]);

    useEffect(() => {
        if (!viewerShellRef.current) return;
        const ro = new ResizeObserver(() => recomputeRenderSize());
        ro.observe(viewerShellRef.current);
        return () => ro.disconnect();
    }, [recomputeRenderSize]);

    useEffect(() => {
        recomputeRenderSize();
    }, [rotation, imageUrl, recomputeRenderSize]);

    useEffect(() => {
        const onKey = e => {
            if (!selectedImage) return;
            if (isTypingInField()) return;
            if (e.key === "ArrowRight") {
                e.preventDefault();
                handleNext();
            } else if (e.key === "ArrowLeft") {
                e.preventDefault();
                handlePrev();
            } else if (e.key === "Escape") {
                e.preventDefault();
                onCancel?.();
            } else if (e.key.toLowerCase() === "f") {
                e.preventDefault();
                toggleFullscreen();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [selectedImage, handleNext, handlePrev, onCancel, toggleFullscreen]);

    if (!selectedImage) return null;

    const title = selectedImage?.description || selectedImage?.caption || "-";

    return (
        <Root>
            <Backdrop $noOverlay={noOverlay}>
                <Draggable handle=".drag-handle" cancel=".not-draggable" bounds="parent" defaultPosition={{ x: 0, y: 0 }}>
                    <Dialog role="document">
                        <Content ref={viewerContainerRef} $noOverlay={noOverlay}>
                            {hasNextPrev && imageList?.length > 1 && (
                                <>
                                    <ArrowButton side="left" disabled={getIdx(selectedImage) === 0} onClick={handlePrev} className="not-draggable">
                                        <i className="fas fa-chevron-left" />
                                    </ArrowButton>
                                    <ArrowButton
                                        side="right"
                                        disabled={getIdx(selectedImage) === imageList.length - 1}
                                        onClick={handleNext}
                                        className="not-draggable"
                                    >
                                        <i className="fas fa-chevron-right" />
                                    </ArrowButton>
                                </>
                            )}

                            <Header className="drag-handle" $noOverlay={noOverlay}>
                                <Title title={title}>{title}</Title>
                                <HeaderActions className="not-draggable">
                                    <FsBtn onClick={toggleFullscreen} aria-label="Toggle fullscreen">
                                        <i className={isFullscreen ? "fas fa-compress" : "fas fa-expand"} />
                                    </FsBtn>
                                    <CloseBtn onClick={onCancel} aria-label="Close">
                                        <img src="/img/close.svg" alt="Close" />
                                    </CloseBtn>
                                </HeaderActions>
                            </Header>

                            <Body className="region-otr not-draggable">
                                <ReactTooltip
                                    id="image-view-modal"
                                    effect="solid"
                                    backgroundColor="#007bff"
                                    getContent={d => d?.replace(/(?:\r\n|\r|\n)/g, "<br>")}
                                    html
                                    multiline
                                />

                                <TransformWrapper
                                    key={`tw-${twEpoch}-${isFullscreen ? "fs" : "win"}-${selectedImage?.id ?? selectedImage?._id ?? imageUrl}`}
                                    defaultScale={1}
                                    wheel={{ step: 1.2 }}
                                    doubleClick={{ mode: "zoomIn", step: 1.2 }}
                                    minScale={0.5}
                                    maxScale={8}
                                    limitToBounds={false}
                                >
                                    {({ zoomIn, zoomOut, resetTransform }) => {
                                        twResetRef.current = resetTransform;
                                        return (
                                            <>
                                                <ViewerShell ref={viewerShellRef}>
                                                    <TransformComponent
                                                        wrapperStyle={{ width: "100%", height: "100%" }}
                                                        contentStyle={{ width: "100%", height: "100%" }}
                                                    >
                                                        <CenterBox>
                                                            <RotatingFrame style={{ transform: `rotate(${rotation}deg)` }}>
                                                                <Img
                                                                    src={imageUrl}
                                                                    alt=""
                                                                    draggable={false}
                                                                    style={{
                                                                        width: renderSize.w ? `${renderSize.w}px` : "auto",
                                                                        height: renderSize.h ? `${renderSize.h}px` : "auto"
                                                                    }}
                                                                />
                                                            </RotatingFrame>
                                                        </CenterBox>
                                                    </TransformComponent>
                                                </ViewerShell>

                                                <ToolbarContainer>
                                                    <ToolButton onClick={() => zoomIn(0.6)} aria-label="Zoom In">
                                                        <i className="fas fa-search-plus" />
                                                    </ToolButton>
                                                    <ToolButton onClick={() => zoomOut(0.6)} aria-label="Zoom Out">
                                                        <i className="fas fa-search-minus" />
                                                    </ToolButton>
                                                    <ToolButton onClick={rotateLeft} aria-label="Rotate Left">
                                                        <i className="fas fa-undo" />
                                                    </ToolButton>
                                                    <ToolButton onClick={rotateRight} aria-label="Rotate Right">
                                                        <i className="fas fa-redo" />
                                                    </ToolButton>
                                                    <ToolButton onClick={resetRotation} aria-label="Reset">
                                                        <i className="fas fa-sync-alt" />
                                                    </ToolButton>
                                                </ToolbarContainer>
                                            </>
                                        );
                                    }}
                                </TransformWrapper>

                                <ThumbBarWrap $noOverlay={noOverlay}>
                                    <ThumbRow ref={thumbRowRef}>
                                        {imageList.map(img => {
                                            const isActive = (img?.id ?? img?._id) === (selectedImage?.id ?? selectedImage?._id);
                                            const imgMaster = isSurveyAssetView ? img : img?.master_image;
                                            const tUrl = stripPossibleThumb(imgMaster);
                                            const fallbackUrl = (() => {
                                                const { is_edited, s3_eimage_key, s3_image_key } = imgMaster || {};
                                                return is_edited ? s3_eimage_key : s3_image_key;
                                            })();

                                            return (
                                                <ThumbItem
                                                    key={img?.id ?? img?._id}
                                                    $active={isActive}
                                                    onClick={() => handleThumbClick(img)}
                                                    title={img?.description || img?.caption || ""}
                                                    data-for="image-view-modal"
                                                    data-tip={(img?.description || img?.caption || "").slice(0, 100)}
                                                >
                                                    <ThumbImg src={tUrl || fallbackUrl || ""} alt="" loading="lazy" />
                                                </ThumbItem>
                                            );
                                        })}
                                    </ThumbRow>
                                </ThumbBarWrap>
                            </Body>
                        </Content>
                    </Dialog>
                </Draggable>
            </Backdrop>
        </Root>
    );
};

const Root = styled.div``;

const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: ${({ $noOverlay }) => $noOverlay ? 'transparent' : 'rgba(0,0,0,0.3)'};
    z-index: 1050;
`;

const Dialog = styled.div`
    pointer-events: all;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    width: ${({ $noOverlay }) => $noOverlay ? 'clamp(420px, 33vw, 760px)' : 'clamp(420px, 48vw, 760px)'};
    max-width: 100%;
    height: 70vh;
    overflow: hidden;
    border-radius: 10px;
    background: #fff;
    position: relative;
    border:  ${({ $noOverlay }) => $noOverlay ? '1px solid #97999c' : 'none'};
    box-shadow:  ${({ $noOverlay }) => $noOverlay ? '0 12px 28px rgba(0, 0, 0, 0.18)' : 'none'};

    @media (max-width: 768px) {
        width: 92vw;
        height: 70vh;
    }
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid #e6e6e6;
    flex: 0 0 auto;
    cursor: move;
    background: ${({ $noOverlay }) => $noOverlay ? '#eaf1f5' : ''};
`;

const Title = styled.h5`
    margin: 0;
    font-size: 16px;
    font-weight: 500;
    color: #333;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const HeaderActions = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`;

const iconBtn = css`
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    i {
        color: #3a3a3a;
        font-size: 16px;
    }
    img {
        width: 18px;
        height: 18px;
        opacity: 0.7;
    }
    &:hover img {
        opacity: 1;
    }
`;

const CloseBtn = styled.button`
    ${iconBtn}
`;
const FsBtn = styled.button`
    ${iconBtn}
`;

const Body = styled.div`
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    padding-bottom: 0;
    min-height: 0;
    overflow: hidden;
`;

const ViewerShell = styled.div`
    position: relative;
    flex: 1 1 0;
    min-height: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #0a0a0a0d;
    border-radius: 6px;
    overflow: hidden;
`;

const CenterBox = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const RotatingFrame = styled.div`
    transform-origin: center center;
    display: inline-block;
    will-change: transform;
`;

const Img = styled.img`
    display: block;
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    user-select: none;
    pointer-events: none;
`;

const ToolbarContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
    padding: 10px 0;
    background: #f9fafb;
    border-top: 1px solid #e6e6e6;
    border-bottom: 1px solid #e6e6e6;
    flex: 0 0 auto;
`;

const ToolButton = styled.button`
    border: none;
    background: #fff;
    border-radius: 8px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: all 0.2s ease;
    &:hover {
        background: #eaf4ff;
        transform: scale(1.05);
    }
    i {
        color: #216fb7;
        font-size: 16px;
    }
`;

const ThumbBarWrap = styled.div`
    flex: 0 0 auto;
    height: 80px;
    padding: 8px 12px;
    border-top: 1px solid #eee;
    background: ${({ $noOverlay }) => $noOverlay ? '#f9fafb' : '#fff'};
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
    overflow: hidden;
`;

const ThumbRow = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 0;
`;

const ThumbItem = styled.div`
    flex: 0 0 auto;
    width: 60px;
    height: 60px;
    border-radius: 6px;
    overflow: hidden;
    border: ${({ $active }) => ($active ? "2px solid #1383D9" : "1px solid #e5e5e5")};
    box-shadow: ${({ $active }) => ($active ? "0 0 0 2px rgba(19,131,217,0.15) inset" : "none")};
    cursor: pointer;
    background: #f8f8f8;
    position: relative;
`;

const ThumbImg = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
`;

const ArrowButton = styled.button.attrs({ type: "button" })`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    ${({ side }) => (side === "left" ? "left: 8px;" : "right: 8px;")}
    width: 36px;
    height: 36px;
    border-radius: 18px;
    border: none;
    background: rgba(255, 255, 255, 0.9);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 2;
    &:disabled {
        opacity: 0.5;
        cursor: default;
    }
`;
